export type AdminResourceCategory = "Visibility" | "Vibe" | "Customers" | "System" | "Admin";

/** Order categories render in, in both the sidebar and the picker. */
export const ADMIN_RESOURCE_CATEGORY_ORDER: readonly AdminResourceCategory[] = [
  "Visibility",
  "Vibe",
  "Customers",
  "System",
  "Admin"
] as const;

/**
 * What every catalog node carries, at all three depths.
 *
 * `unserved` is the second way a node can be present here and NOT grantable, and
 * it is the opposite case from `navOnly` below. `navOnly` means "the access is
 * real, another resource gates it". `unserved` means **the access does not exist
 * at all**: no backend `@RequireAdmin*` guard and no admin-panel permission check
 * reads this path or anything under it, so a policy that grants it grants nothing.
 *
 * The picker rendering such a leaf is not cosmetic. `resolveAdminPermission`
 * cascades a grant from a PARENT down to its children and never upwards, so
 * granting `admin:twilio-console:usage:view` alone leaves every endpoint closed —
 * the operator ticked a box, was told they had the capability, and does not.
 * `platform-operator-keys` states the same reasoning in prose for its deliberately
 * absent `mint` action: *"a promise the system cannot keep"*. This field is that
 * prose made mechanical, for the case where the leaf already exists.
 *
 * The value is the REASON, and it is mandatory rather than a boolean, because the
 * marker has to say which of the two futures it is: a capability nobody has built
 * yet (delete the marker when the endpoint lands) or a leaf that was never real
 * (delete the leaf). A bare `true` records neither and reads as permanent.
 *
 * Kept honest from both directions by
 * the backend's `admin-catalog-grants-are-served.spec.ts`: a marked node that
 * IS enforced fails, so the marker cannot outlive the gap it describes.
 */
interface AdminResourceNodeDef {
  id: string;
  name: string;
  description: string;
  unserved?: string;
}

export interface AdminResourceActionDef extends AdminResourceNodeDef {}

export interface AdminResourceBlockDef extends AdminResourceNodeDef {
  actions?: AdminResourceActionDef[];
}

export interface AdminResourceItemDef extends AdminResourceNodeDef {
  category: AdminResourceCategory;
  actions?: AdminResourceActionDef[];
  blocks?: AdminResourceBlockDef[];
  /**
   * The section appears in the sidebar but is NOT grantable: access to it is gated
   * by a DIFFERENT resource (see the permission map in `admin-navigation.ts`).
   *
   * Sidebar presence and grantability are separate axes. Without this flag the only
   * way to give a section a sidebar entry is to make it grantable, so an aliased
   * section ends up offered in the policy editor while granting it does nothing —
   * the picker promising access it cannot deliver. `flattenAdminResourceCatalog`
   * skips these (with their actions and blocks), so they never reach the grantable
   * set, the seeded registry, the picker, or the `AdminResourceKey` union.
   */
  navOnly?: boolean;
}
